import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import makeStyles from "@material-ui/core/styles/makeStyles";
import BackIcon from "@material-ui/icons/ArrowBack";
import { IconButton, InputAdornment } from "@material-ui/core";
import API from "../../../utils/axios";
import { emailRegex } from "../../../utils/regExps";
import BlackButton from "../BlackButton";
import { Link as RouterLink } from "react-router-dom";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import { useSnackbar } from "notistack";
import Form from "../../common/Form";

const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    boxSizing: "border-box",
    borderBottom: theme.variables.border
  },
  title: {
    fontWeight: 500,
    fontSize: "1.25rem",
    lineHeight: "1.4375rem"
  },
  iconBtn: {
    padding: theme.spacing(0.75),
    "&:hover": {
      backgroundColor: "#EEE"
    }
  }
}));

function ChangeEmail({ userData, setLoading, setUser }) {
  const classes = useStyles();

  const [saveDisabled, setSaveDisabled] = useState(true);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const [inputValues, setInputValues] = useState({
    email: userData.email || "",
    newEmail: "",
    password: ""
  });

  const [errors, setErrors] = useState({
    email: "",
    newEmail: "",
    password: ""
  });

  const { enqueueSnackbar } = useSnackbar();

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    setSaveDisabled(false);
  };

  const checkForErrors = () => {
    const newErrors = {
      email: !emailRegex.test(inputValues.email),
      newEmail: !emailRegex.test(inputValues.newEmail)
        ? "Choose a valid email address"
        : "",
      password: inputValues.password ? "" : "Please fill in this field"
    };

    setErrors(newErrors);

    return Object.values(newErrors).some(val => val);
  };

  const handleSave = e => {
    e.preventDefault();

    if (checkForErrors()) return;
    setLoading(true);
    API.patch(`v1/users/${userData.id}/email`, {
      email: inputValues.newEmail,
      password: inputValues.password
    })
      .then(res => {
        setInputValues({
          email: res.data.email || "",
          newEmail: "",
          password: ""
        });
        setUser(res.data);
        setSaveDisabled(true);
        enqueueSnackbar("Email successfully updated");
      })
      .catch(e => {
        if (e && e.status === 400) {
          console.dir(e);
          const fieldName = e.data && e.data.errors[0].field;
          const defaultMessage = e.data && e.data.errors[0].defaultMessage;
          if (fieldName === "password") {
            setErrors({
              email: "",
              newEmail: "",
              password: defaultMessage
            });
          } else if (fieldName === "email") {
            setErrors({
              email: "",
              newEmail: defaultMessage,
              password: ""
            });
          }
        } else {
          enqueueSnackbar("Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {/* Header */}
      <Box
        className={classes.header}
        display="flex"
        alignItems="center"
        pl={2}
        pr={3}
      >
        <Box className={classes.backButton} mr={2}>
          <IconButton component={RouterLink} to="/account/profile">
            <BackIcon />
          </IconButton>
        </Box>
        <Typography variant="h5" className={classes.title}>
          Change email address
        </Typography>
      </Box>

      {/* Body */}
      <Box
        pt={3}
        pl={3}
        pr={5}
        display="flex"
        flexDirection="column"
        height="100%"
        style={{ boxSizing: "border-box" }}
      >
        {/* Form */}
        <Form
          data={[
            [
              {
                renderInputField: true,
                label: "Current email address",
                name: "email",
                type: "email",
                disabled: true,
                dataCy: "email"
              }
            ],
            [
              {
                renderInputField: true,
                label: "New email address",
                name: "newEmail",
                type: "email",
                dataCy: "newEmail"
              }
            ],
            [
              {
                renderInputField: true,
                label: "Enter current password",
                name: "password",
                type: "password",
                dataCy: "password",
                endAdornment: (
                  <InputAdornment position="end">
                    <OptimizedTooltip
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        className={classes.iconBtn}
                      >
                        {showPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff style={{ color: "#B6B6B6" }} />
                        )}
                      </IconButton>
                    </OptimizedTooltip>
                  </InputAdornment>
                )
              }
            ]
          ]}
          handleChange={handleChange}
          inputValues={inputValues}
          errorValues={errors}
          showPassword={showPassword}
        />
        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" py={1} mt={3}>
          <BlackButton
            handleClick={handleSave}
            disabled={saveDisabled}
            content="Save"
            data-cy="saveEmail"
          />
        </Box>
      </Box>
    </>
  );
}

export default ChangeEmail;
